// Chart ranges of the symbol price chart, shared by the browser and the `/ranges` API so both agree on
// what «this range has data» means.

export const CHART_RANGES = [
    '1h',
    '3h',
    '12h',
    '24h',
    '7d',
    '30d',
    '3m',
    '1y',
    '3y',
    '5y',
] as const;
export type ChartRange = (typeof CHART_RANGES)[number];

/** Length of each range. The APIs often return a longer tail (or a whole year for an unsupported range),
 *  so points are clipped to this window, counted back from the newest point. */
export const CHART_RANGE_SECONDS: Record<ChartRange, number> = {
    '1h': 3600,
    '3h': 3 * 3600,
    '12h': 12 * 3600,
    '24h': 86400,
    '7d': 7 * 86400,
    '30d': 31 * 86400,
    '3m': 92 * 86400,
    '1y': 366 * 86400,
    '3y': 3 * 366 * 86400,
    '5y': 5 * 366 * 86400,
};

/** Range a price chart opens on, best first: a year reads best; shorter ones only when a symbol has no yearly data. */
export const CHART_DEFAULT_ORDER: readonly string[] = ['1y', '3m', '30d', '7d', '24h'];

export const isChartRange = (v: string): v is ChartRange =>
    (CHART_RANGES as readonly string[]).includes(v);

export interface RangePoint<T> {
    timestamp: number; // seconds
    value: number;
    item: T;
}

/**
 * Points of one range, ready to draw: positive values only, oldest → newest, one per timestamp,
 * clipped to the range window. Fewer than 2 points means the range has nothing to show.
 */
export function pointsInRange<T extends { timestamp?: unknown }>(
    items: readonly T[] | null | undefined,
    range: string,
    valueOf: (item: T) => unknown,
): RangePoint<T>[] {
    if (!Array.isArray(items)) return [];
    const mapped: RangePoint<T>[] = [];
    for (const item of items) {
        const ts = Math.floor(Number(item?.timestamp));
        const value = Number(valueOf(item));
        if (!(ts > 0) || !(value > 0) || !isFinite(value)) continue;
        mapped.push({ timestamp: ts, value, item });
    }
    mapped.sort((a, b) => a.timestamp - b.timestamp);

    const unique: RangePoint<T>[] = [];
    for (const p of mapped) {
        if (!unique.length || p.timestamp > unique[unique.length - 1].timestamp) unique.push(p);
    }
    if (!unique.length) return unique;

    const span = isChartRange(range) ? CHART_RANGE_SECONDS[range] : Infinity;
    const from = unique[unique.length - 1].timestamp - span;
    return unique.filter((p) => p.timestamp >= from);
}
